"use client"

import { useState, useEffect, useRef } from "react"
import {
  Tag,
  Grid,
  DollarSign,
  Clock,
  MapPin,
  AlignLeft,
  Home,
  CheckCircle,
  X,
  Search,
  Locate,
  Edit3,
  ArrowLeft,
  Upload,
  Camera,
} from "lucide-react"
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet"
import L from "leaflet"
import logo from "../../assets/logo.png"
import "../Encuesta/Encuesta.css"
import "../RegistroParqueadero/RegistroParqueadero.css"
import "./EditarParqueadero.css"
import edicionParqueaderoService from "../../services/edicionParqueaderoService"
import { uploadImagen } from "../../services/uploadService"

// Tipos y tamaño permitidos (deben coincidir con el backend)
const TIPOS_PERMITIDOS = ["image/jpeg", "image/png", "image/webp"]
const MAX_SIZE_BYTES = 20 * 1024 * 1024 // 20MB

// Icono de marcador Leaflet por defecto
const markerIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

// Captura click y devuelve coords al padre
function LocationPicker({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect([e.latlng.lat, e.latlng.lng])
    },
  })
  return null
}

// Centra el mapa en una ubicación
function MapCenterController({ center, zoom }) {
  const map = useMap()
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom || 15)
    }
  }, [center, zoom, map])
  return null
}

// Modal (reutiliza estilos de Encuesta.css)
function Modal({ isOpen, onClose, type, title, message }) {
  if (!isOpen) return null
  return (
    <div className="modal-overlay-modern" onClick={type !== "loading" ? onClose : undefined}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className={`modal-icon-large ${type}`}>
          {type === "success" && <CheckCircle size={32} />}
          {type === "error" && <X size={32} />}
          {type === "loading" && <div className="modal-spinner-large"></div>}
        </div>
        <div className="modal-content-modern">
          <h2 className="modal-title-large">{title}</h2>
          <p className="modal-message">{message}</p>
        </div>
        {type !== "loading" && (
          <button className={`btn-modal-modern ${type}`} onClick={onClose}>
            {type === "success" ? "¡Perfecto!" : "Entendido"}
          </button>
        )}
      </div>
    </div>
  )
}

function FormStep({ children, isActive }) {
  if (!isActive) return null
  return <div className="form-step">{children}</div>
}

const emptyForm = {
  nombre: "",
  ubicacion: "",
  preciotarifa: "",
  plazas: "",
  horaApertura: "",
  horaCierre: "",
  descripcion: "",
  latitud: null,
  longitud: null,
}

export default function EditarParqueadero() {
  // Lista de sucursales corregibles
  const [sucursales, setSucursales] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [listError, setListError] = useState("")
  const [busqueda, setBusqueda] = useState("") // filtro por nombre

  // Sucursal seleccionada para editar
  const [selected, setSelected] = useState(null) // objeto sucursal
  const [form, setForm] = useState(emptyForm)
  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 3

  // Fotos de la sucursal seleccionada (se gestionan al instante contra el backend)
  const [fotos, setFotos] = useState([]) // [{ id, fotografia }]
  const [uploadCount, setUploadCount] = useState(0) // subidas en curso
  const fileInputRef = useRef(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [modal, setModal] = useState({ isOpen: false, type: "success", title: "", message: "" })

  // Mapa / búsqueda
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [showSearchResults, setShowSearchResults] = useState(false)

  const defaultCenter = [-0.1807, -78.4678] // Quito

  const showModal = (type, title, message) => setModal({ isOpen: true, type, title, message })
  const closeModal = () => setModal((prev) => ({ ...prev, isOpen: false }))

  // Cargar la lista al montar
  const cargarLista = async () => {
    setLoadingList(true)
    setListError("")
    try {
      const data = await edicionParqueaderoService.list()
      setSucursales(data)
    } catch (error) {
      setListError(error.message || "No se pudo cargar la lista.")
    } finally {
      setLoadingList(false)
    }
  }

  useEffect(() => {
    cargarLista()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleMapSelect = ([lat, lng]) => {
    setForm((prev) => ({ ...prev, latitud: lat, longitud: lng }))
    reverseGeocode(lat, lng)
  }

  // Rellena la dirección automáticamente según las coordenadas (Nominatim reverse).
  const reverseGeocode = async (lat, lng) => {
    const fallback = `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`
    setForm((prev) => ({ ...prev, ubicacion: fallback }))
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      )
      if (!res.ok) throw new Error("reverse geocoding failed")
      const data = await res.json()
      if (data && data.display_name) {
        setForm((prev) => ({ ...prev, ubicacion: data.display_name.split(",").slice(0, 4).join(", ") }))
      }
    } catch (error) {
      console.error("Error en reverse geocoding:", error)
    }
  }

  // Seleccionar una sucursal: carga sus datos frescos y precarga el formulario.
  const elegirSucursal = async (sucursal) => {
    showModal("loading", "Cargando", "Obteniendo los datos de la sucursal...")
    try {
      const fresca = await edicionParqueaderoService.getOne(sucursal.id)
      setSelected(fresca)
      setForm(edicionParqueaderoService.toForm(fresca))
      setCurrentStep(1)
      await cargarFotos(sucursal.id)
      closeModal()
      window.scrollTo(0, 0)
    } catch (error) {
      showModal("error", "Error", `No se pudieron cargar los datos: ${error.message}`)
    }
  }

  const cargarFotos = async (sucursalId) => {
    try {
      const data = await edicionParqueaderoService.listFotos(sucursalId)
      setFotos(data)
    } catch (error) {
      console.error("Error al cargar fotos:", error)
      setFotos([])
    }
  }

  const volverALista = () => {
    setSelected(null)
    setForm(emptyForm)
    setFotos([])
    setCurrentStep(1)
    setSearchQuery("")
    setShowSearchResults(false)
    window.scrollTo(0, 0)
  }

  // ---- Gestión de fotos (subir mejores / eliminar) ----
  const openGallery = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  // Sube un archivo, obtiene su URL y la asocia a la sucursal (persiste al instante).
  const uploadOne = async (file) => {
    if (!TIPOS_PERMITIDOS.includes(file.type)) {
      showModal("error", "Formato no permitido", `"${file.name}" no es una imagen JPG, PNG o WEBP.`)
      return
    }
    if (file.size > MAX_SIZE_BYTES) {
      showModal("error", "Imagen muy grande", `"${file.name}" supera el máximo de 20 MB.`)
      return
    }
    setUploadCount((n) => n + 1)
    try {
      const url = await uploadImagen(file)
      const creada = await edicionParqueaderoService.addFoto(selected.id, url)
      setFotos((prev) => [...prev, creada])
    } catch (error) {
      showModal("error", "Error al subir", `No se pudo subir "${file.name}": ${error.message}`)
    } finally {
      setUploadCount((n) => Math.max(0, n - 1))
    }
  }

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = "" // permitir volver a elegir el mismo archivo
    for (const file of files) {
      await uploadOne(file)
    }
  }

  const handleDeleteFoto = async (fotoId) => {
    const prev = fotos
    setFotos((f) => f.filter((x) => x.id !== fotoId)) // optimista
    try {
      await edicionParqueaderoService.deleteFoto(selected.id, fotoId)
    } catch (error) {
      setFotos(prev) // revertir si falla
      showModal("error", "Error al eliminar", `No se pudo eliminar la foto: ${error.message}`)
    }
  }

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
      window.scrollTo(0, 0)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      window.scrollTo(0, 0)
    }
  }

  // Ubicación actual del usuario
  const getCurrentLocation = () => {
    setIsGettingLocation(true)
    if (!navigator.geolocation) {
      showModal("error", "Geolocalización no disponible", "Tu navegador no soporta geolocalización.")
      setIsGettingLocation(false)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setForm((prev) => ({ ...prev, latitud: latitude, longitud: longitude }))
        reverseGeocode(latitude, longitude)
        setIsGettingLocation(false)
        showModal("success", "¡Ubicación Obtenida!", "Se ha establecido tu ubicación actual correctamente.")
      },
      (error) => {
        let errorMessage = "No se pudo obtener tu ubicación."
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Permiso de ubicación denegado. Por favor, permite el acceso a tu ubicación."
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Información de ubicación no disponible."
            break
          case error.TIMEOUT:
            errorMessage = "Tiempo de espera agotado para obtener la ubicación."
            break
          default:
            break
        }
        showModal("error", "Error de Ubicación", errorMessage)
        setIsGettingLocation(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    )
  }

  // Buscar direcciones (Nominatim)
  const searchLocation = async (query) => {
    if (!query.trim()) return
    setIsSearching(true)
    setShowSearchResults(false)
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=ec&addressdetails=1`,
      )
      if (!response.ok) throw new Error("Error en la búsqueda")
      const results = await response.json()
      setSearchResults(results)
      setShowSearchResults(results.length > 0)
      if (results.length === 0) {
        showModal("error", "Sin Resultados", "No se encontraron ubicaciones para tu búsqueda.")
      }
    } catch (error) {
      showModal("error", "Error de Búsqueda", "No se pudo realizar la búsqueda. Inténtalo de nuevo.")
    } finally {
      setIsSearching(false)
    }
  }

  const selectSearchResult = (result) => {
    const lat = Number.parseFloat(result.lat)
    const lon = Number.parseFloat(result.lon)
    setForm((prev) => ({
      ...prev,
      latitud: lat,
      longitud: lon,
      ubicacion: result.display_name.split(",").slice(0, 4).join(", "),
    }))
    setShowSearchResults(false)
    setSearchQuery("")
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      searchLocation(searchQuery)
    }
  }

  const canSave = () =>
    form.nombre.trim() &&
    form.preciotarifa !== "" &&
    Number(form.preciotarifa) >= 0 &&
    form.plazas !== "" &&
    Number(form.plazas) >= 1 &&
    form.latitud &&
    form.longitud

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!canSave()) {
      showModal("error", "Datos incompletos", "Revisa nombre, plazas, tarifa y la ubicación en el mapa.")
      return
    }

    setIsSubmitting(true)
    showModal("loading", "Guardando cambios", "Por favor espera mientras actualizamos la información...")

    try {
      const payload = edicionParqueaderoService.formatData(form)
      await edicionParqueaderoService.update(selected.id, payload)
      setTimeout(() => {
        showModal("success", "¡Cambios Guardados!", "La información de la sucursal se actualizó correctamente.")
        cargarLista() // refrescar la lista con los datos nuevos
      }, 800)
    } catch (error) {
      console.error("Error al actualizar la sucursal:", error)
      showModal("error", "Error al Guardar", `No se pudo actualizar la sucursal: ${error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Filtro por nombre (parcial o completo, sin distinguir mayúsculas/acentos básicos)
  const termino = busqueda.trim().toLowerCase()
  const sucursalesFiltradas = termino
    ? sucursales.filter((s) => (s.nombre || "").toLowerCase().includes(termino))
    : sucursales

  return (
    <div className="encuesta-container">
      <header className="encuesta-header">
        <img src={logo || "/placeholder.svg"} alt="Parking Club" className="encuesta-logo" />
        <h1 className="encuesta-title">
          Parking <span className="title-club">Club</span>
        </h1>
      </header>

      {/* ===== VISTA 1: LISTA DE SUCURSALES ===== */}
      {!selected && (
        <div className="encuesta-form">
          <h2 className="step-title">Editar sucursales</h2>
          <p className="step-description">Selecciona la sucursal cuya información quieres corregir</p>

          {loadingList && (
            <p className="url-hint" style={{ marginTop: "1rem" }}>
              <span className="spinner"></span> Cargando sucursales...
            </p>
          )}

          {listError && !loadingList && (
            <div className="cam-error" style={{ marginTop: "1rem" }}>
              {listError}
              <div style={{ marginTop: "1rem" }}>
                <button type="button" className="btn-next" onClick={cargarLista}>
                  Reintentar
                </button>
              </div>
            </div>
          )}

          {!loadingList && !listError && sucursales.length === 0 && (
            <p className="url-hint" style={{ marginTop: "1rem" }}>
              No hay sucursales disponibles para editar.
            </p>
          )}

          {!loadingList && !listError && sucursales.length > 0 && (
            <>
              {/* Buscador por nombre (parcial o completo) */}
              <div className="buscador-sucursal">
                <Search size={18} className="buscador-icon" />
                <input
                  type="text"
                  className="buscador-input"
                  placeholder="Buscar parqueadero por nombre..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
                {busqueda && (
                  <button
                    type="button"
                    className="buscador-clear"
                    onClick={() => setBusqueda("")}
                    aria-label="Limpiar búsqueda"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Contador de parqueaderos visibles */}
              <div className="sucursales-contador">
                {termino
                  ? `${sucursalesFiltradas.length} de ${sucursales.length} parqueaderos`
                  : `${sucursales.length} ${sucursales.length === 1 ? "parqueadero" : "parqueaderos"}`}
              </div>

              {sucursalesFiltradas.length === 0 ? (
                <p className="url-hint" style={{ marginTop: "1rem" }}>
                  No se encontraron parqueaderos que coincidan con "{busqueda}".
                </p>
              ) : (
                <div className="sucursales-lista">
                  {sucursalesFiltradas.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      className="sucursal-item"
                      onClick={() => elegirSucursal(s)}
                    >
                      <div className="sucursal-item-info">
                        <div className="sucursal-item-nombre">
                          <Home size={16} /> {s.nombre}
                        </div>
                        <div className="sucursal-item-ubicacion">
                          <MapPin size={14} /> {s.ubicacion || "Sin dirección"}
                        </div>
                      </div>
                      <span className="sucursal-item-editar">
                        <Edit3 size={16} /> Editar
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== VISTA 2: FORMULARIO DE EDICIÓN ===== */}
      {selected && (
        <form className="encuesta-form" onSubmit={handleSubmit}>
          <button type="button" className="btn-back" onClick={volverALista} style={{ marginBottom: "1rem" }}>
            <ArrowLeft size={16} style={{ marginRight: "0.4rem" }} /> Volver a la lista
          </button>

          {/* Indicador de progreso */}
          <div className="progress-container">
            <div className="progress-steps">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`progress-step ${currentStep === step ? "active" : ""} ${currentStep > step ? "completed" : ""}`}
                >
                  <div className="step-number">{step}</div>
                  <div className="step-label">
                    {step === 1 && "Datos"}
                    {step === 2 && "Fotos"}
                    {step === 3 && "Ubicación"}
                  </div>
                </div>
              ))}
              <div className="progress-line">
                <div
                  className="progress-line-inner"
                  style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* PASO 1: Datos */}
          <FormStep isActive={currentStep === 1}>
            <h2 className="step-title">Datos del parqueadero</h2>
            <p className="step-description">
              Editando: <strong>{selected.nombre}</strong> (ID {selected.id})
            </p>

            <div className="form-grid">
              <div className="form-column">
                <div className="form-group">
                  <label htmlFor="nombre">
                    <Home size={18} /> Nombre del Parqueadero
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={form.nombre}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    placeholder="Nombre del parqueadero"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="plazas">
                    <Grid size={18} /> Plazas Disponibles
                  </label>
                  <input
                    type="number"
                    id="plazas"
                    name="plazas"
                    value={form.plazas}
                    onChange={handleChange}
                    required
                    min="1"
                    disabled={isSubmitting}
                    placeholder="Cantidad de espacios"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="preciotarifa">
                    <DollarSign size={18} /> Tarifa por hora ($)
                  </label>
                  <input
                    type="number"
                    id="preciotarifa"
                    name="preciotarifa"
                    value={form.preciotarifa}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    disabled={isSubmitting}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-column">
                <div className="form-row">
                  <div className="form-group half">
                    <label htmlFor="horaApertura">
                      <Clock size={18} /> Hora de Apertura
                    </label>
                    <input
                      type="time"
                      id="horaApertura"
                      name="horaApertura"
                      value={form.horaApertura}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="form-group half">
                    <label htmlFor="horaCierre">
                      <Clock size={18} /> Hora de Cierre
                    </label>
                    <input
                      type="time"
                      id="horaCierre"
                      name="horaCierre"
                      value={form.horaCierre}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion">
                    <AlignLeft size={18} /> Descripción Adicional
                  </label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={form.descripcion}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe características especiales, servicios adicionales, etc."
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn-back" onClick={volverALista}>
                Cancelar
              </button>
              <button type="button" className="btn-next" onClick={nextStep}>
                Siguiente
              </button>
            </div>
          </FormStep>

          {/* PASO 2: Fotos */}
          <FormStep isActive={currentStep === 2}>
            <h2 className="step-title">Fotos del parqueadero</h2>
            <p className="step-description">
              Elimina las fotos malas y sube mejores. Los cambios se guardan al instante.
            </p>

            <div className="form-grid">
              <div className="form-column full-width">
                <p className="url-hint">
                  Sube imágenes desde tu dispositivo (JPG, PNG o WEBP, máx. 20 MB cada una). Para reemplazar una foto,
                  elimínala y sube la mejor.
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleFiles}
                  style={{ display: "none" }}
                />

                <div className="photo-actions">
                  <button type="button" className="btn-add-url" onClick={openGallery} disabled={isSubmitting}>
                    <Upload size={16} /> Subir mejor foto
                  </button>
                </div>

                <div className="photo-grid">
                  {fotos.map((foto, index) => (
                    <div className="photo-thumb" key={foto.id}>
                      <img src={foto.fotografia} alt={`Foto ${index + 1}`} />
                      <button
                        type="button"
                        className="photo-remove"
                        onClick={() => handleDeleteFoto(foto.id)}
                        disabled={isSubmitting}
                        title="Eliminar foto"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {Array.from({ length: uploadCount }).map((_, i) => (
                    <div className="photo-thumb photo-uploading" key={`uploading-${i}`}>
                      <div className="spinner"></div>
                    </div>
                  ))}
                </div>

                {fotos.length === 0 && uploadCount === 0 && (
                  <p className="url-hint" style={{ marginTop: "1rem" }}>
                    <Camera size={16} style={{ verticalAlign: "middle", marginRight: "0.4rem" }} />
                    Esta sucursal aún no tiene fotos.
                  </p>
                )}
              </div>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn-back" onClick={prevStep}>
                Atrás
              </button>
              <button type="button" className="btn-next" onClick={nextStep} disabled={uploadCount > 0}>
                Siguiente
              </button>
            </div>
          </FormStep>

          {/* PASO 3: Ubicación */}
          <FormStep isActive={currentStep === 3}>
            <h2 className="step-title">Ubicación en el mapa</h2>
            <p className="step-description">Corrige la ubicación exacta del parqueadero</p>

            <div className="form-grid">
              <div className="form-column full-width">
                <div className="location-controls">
                  <button
                    type="button"
                    className={`btn-location ${isGettingLocation ? "loading" : ""}`}
                    onClick={getCurrentLocation}
                    disabled={isSubmitting || isGettingLocation}
                  >
                    <Locate size={16} />
                    {isGettingLocation ? "Obteniendo..." : "Mi Ubicación"}
                  </button>

                  <div className="search-container">
                    <div className="search-input-wrapper">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Buscar dirección..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                        disabled={isSubmitting}
                        className="search-input"
                      />
                      <button
                        type="button"
                        className={`btn-search ${isSearching ? "loading" : ""}`}
                        onClick={() => searchLocation(searchQuery)}
                        disabled={isSubmitting || isSearching || !searchQuery.trim()}
                        aria-label="Buscar dirección"
                      >
                        {isSearching ? <div className="spinner"></div> : <Search size={18} />}
                      </button>
                    </div>

                    {showSearchResults && (
                      <div className="search-results">
                        {searchResults.map((result, index) => (
                          <div key={index} className="search-result-item" onClick={() => selectSearchResult(result)}>
                            <MapPin size={14} />
                            <div className="result-info">
                              <div className="result-name">{result.display_name.split(",")[0]}</div>
                              <div className="result-address">{result.display_name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="map-wrapper">
                  <MapContainer
                    center={form.latitud && form.longitud ? [form.latitud, form.longitud] : defaultCenter}
                    zoom={13}
                    scrollWheelZoom={false}
                    style={{ height: "300px", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapCenterController
                      center={form.latitud && form.longitud ? [form.latitud, form.longitud] : defaultCenter}
                      zoom={15}
                    />
                    <LocationPicker onSelect={handleMapSelect} />
                    {form.latitud && form.longitud && (
                      <Marker position={[form.latitud, form.longitud]} icon={markerIcon} />
                    )}
                  </MapContainer>
                </div>

                <div className="coords-display">
                  {form.latitud && form.longitud
                    ? `Coordenadas: ${Number(form.latitud).toFixed(5)}, ${Number(form.longitud).toFixed(5)}`
                    : "Usa 'Mi Ubicación', busca una dirección, o haz clic en el mapa"}
                </div>

                <div className="form-group" style={{ marginTop: "1.5rem" }}>
                  <label htmlFor="ubicacion">
                    <MapPin size={18} /> Dirección detectada
                  </label>
                  <input
                    type="text"
                    id="ubicacion"
                    name="ubicacion"
                    value={form.ubicacion}
                    readOnly
                    placeholder="Se completará automáticamente al elegir la ubicación en el mapa"
                  />
                </div>
              </div>
            </div>

            <div className="form-navigation">
              <button type="button" className="btn-back" onClick={prevStep}>
                Atrás
              </button>
              <button
                type="submit"
                className={`btn-submit-step ${isSubmitting ? "loading" : ""}`}
                disabled={isSubmitting || !canSave()}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Tag size={18} style={{ marginRight: "0.5rem" }} />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </FormStep>
        </form>
      )}

      <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} />
    </div>
  )
}
